// P5 — Withdrawals queue admin page. Mirrors JobCategoriesList layout
// (TVP2 §9 styling). Default filter shows requested + processing rows.
//
// Detail dialog shows full bank info (decrypted server-side) + action
// buttons: Mark processing | Mark completed | Reject (with reason).

import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { tokens } from "@findgeeks/ui-tokens";
import { ApiError, apiFetch } from "@/lib/api";
import { ENV } from "@/lib/env";
import { supabase } from "@/lib/supabase";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { useToast } from "@/components/Toast";
import { usePagedQuery } from "@/hooks/usePagedQuery";

interface WithdrawalRow {
  id: string;
  user_id: string;
  amount_cents: number;
  fee_cents: number;
  currency: string;
  status: "requested" | "processing" | "completed" | "rejected" | "cancelled";
  destination_summary: string;
  requested_at: string;
  processed_at: string | null;
  admin_notes: string | null;
  rejection_reason: string | null;
  user: { id: string; display_name: string | null; email: string | null } | null;
}

interface WithdrawalDetail extends WithdrawalRow {
  destination_details: {
    bank_name?: string;
    account_holder?: string;
    account_number?: string;
  } | null;
}

function formatHKD(cents: number): string {
  return `HK$${(cents / 100).toLocaleString("en-HK")}`;
}

function friendlyError(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return err instanceof Error ? err.message : fallback;
  const code =
    (err.body && typeof err.body === "object" && "error" in err.body
      ? String((err.body as { error?: unknown }).error ?? "")
      : "") || err.message;
  return code.replace(/_/g, " ") || err.message || fallback;
}

export function WithdrawalsList() {
  const { show, ToastEl } = useToast();
  const qc = useQueryClient();
  const [statusQ, setStatusQ] = useState<"" | "requested" | "processing" | "completed" | "rejected" | "cancelled">(
    "requested",
  );
  const [filters, setFilters] = useState<{ status?: string }>({ status: "requested" });
  const [openId, setOpenId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);

  const list = usePagedQuery<WithdrawalRow>("withdrawals", {
    filters,
    initialPagination: { page: 0, pageSize: 10 },
  });

  const detail = useQuery<{ withdrawal: WithdrawalDetail }>({
    queryKey: ["admin", "withdrawal", openId],
    queryFn: () => apiFetch<{ withdrawal: WithdrawalDetail }>(`/v1/admin/withdrawals/${openId}`),
    enabled: Boolean(openId),
  });

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    setFilters({ status: statusQ || undefined });
    list.setPaginationModel({ ...list.paginationModel, page: 0 });
  };
  const onReset = () => {
    setStatusQ("");
    setFilters({});
    list.setPaginationModel({ ...list.paginationModel, page: 0 });
  };

  // Mirrors BillingReport.exportCsv — fetches a server-built CSV with the
  // current status filter so the admin can process bank transfers offline.
  // Decrypted bank details are baked into the CSV server-side (the list
  // response intentionally doesn't carry them).
  const exportCsv = async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const params = new URLSearchParams();
      if (filters.status) params.set("status", filters.status);
      const qs = params.toString();
      const res = await fetch(
        `${ENV.API_BASE_URL}/v1/admin/withdrawals.csv${qs ? `?${qs}` : ""}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        },
      );
      if (!res.ok) {
        show("CSV export failed", "error");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `withdrawals_${filters.status ?? "all"}_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      show("CSV downloaded", "success");
    } catch (err) {
      show(err instanceof Error ? err.message : "CSV failed", "error");
    }
  };

  const markProcessing = useMutation({
    mutationFn: () =>
      apiFetch(`/v1/admin/withdrawals/${openId}/processing`, { method: "POST" }),
    onSuccess: () => {
      show("Marked as processing", "success");
      list.invalidate();
      qc.invalidateQueries({ queryKey: ["admin", "withdrawal", openId] });
    },
    onError: (err) => show(friendlyError(err, "Failed"), "error"),
  });
  const markCompleted = useMutation({
    mutationFn: () =>
      apiFetch(`/v1/admin/withdrawals/${openId}/complete`, {
        method: "POST",
        body: {},
      }),
    onSuccess: () => {
      show("Marked as completed", "success");
      setOpenId(null);
      list.invalidate();
    },
    onError: (err) => show(friendlyError(err, "Failed"), "error"),
  });
  const rejectMut = useMutation({
    mutationFn: () =>
      apiFetch(`/v1/admin/withdrawals/${openId}/reject`, {
        method: "POST",
        body: { reason: rejectReason },
      }),
    onSuccess: () => {
      show("Rejected (refunded)", "success");
      setRejectOpen(false);
      setOpenId(null);
      setRejectReason("");
      list.invalidate();
    },
    onError: (err) => show(friendlyError(err, "Failed"), "error"),
  });

  const columns: DataTableColumn<WithdrawalRow>[] = [
    {
      field: "user_id",
      headerName: "Customer",
      render: (r) => r.user?.display_name ?? r.user_id.slice(0, 8),
    },
    {
      field: "amount_cents",
      headerName: "Amount",
      render: (r) => formatHKD(r.amount_cents),
    },
    {
      field: "fee_cents",
      headerName: "Fee",
      render: (r) => (r.fee_cents > 0 ? formatHKD(r.fee_cents) : "—"),
    },
    { field: "destination_summary", headerName: "Destination" },
    {
      field: "requested_at",
      headerName: "Requested",
      render: (r) => new Date(r.requested_at).toLocaleString("en-HK"),
    },
    { field: "status", headerName: "Status" },
  ];

  const baseBtn = {
    px: 4,
    textTransform: "none" as const,
    fontWeight: tokens.font.weights.medium,
    color: "#fff",
    boxShadow: "none",
  };
  const searchBtn = {
    ...baseBtn,
    backgroundColor: "#007bff",
    "&:hover": { backgroundColor: "#0069d9", boxShadow: "none" },
  };
  const greyBtn = {
    ...baseBtn,
    backgroundColor: "#D8D8D8",
    "&:hover": { backgroundColor: "#C8C8C8", boxShadow: "none" },
  };
  const greenBtn = {
    ...baseBtn,
    backgroundColor: "#5CB85C",
    "&:hover": { backgroundColor: "#4CAE4C", boxShadow: "none" },
  };
  const redBtn = {
    ...baseBtn,
    backgroundColor: "#D9534F",
    "&:hover": { backgroundColor: "#C9302C", boxShadow: "none" },
  };

  const w = detail.data?.withdrawal;

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          border: `1px solid ${tokens.colors.admin.border}`,
          borderRadius: 1,
        }}
      >
        <Tabs
          value="search"
          sx={{
            minHeight: 38,
            borderBottom: `1px solid ${tokens.colors.admin.border}`,
          }}
        >
          <Tab value="search" iconPosition="start" icon={<SearchIcon />} label="Search" />
        </Tabs>
        <Box component="form" onSubmit={onSearch} sx={{ p: 3 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3, mb: 3 }}>
            <TextField
              label="Status"
              variant="standard"
              select
              fullWidth
              value={statusQ}
              onChange={(e) => setStatusQ(e.target.value as typeof statusQ)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="requested">Requested</MenuItem>
              <MenuItem value="processing">Processing</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </TextField>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button type="submit" variant="contained" sx={searchBtn}>
              Search
            </Button>
            <Button type="button" variant="contained" onClick={onReset} sx={greyBtn}>
              Reset
            </Button>
            <Button type="button" variant="contained" onClick={exportCsv} sx={greenBtn}>
              Export CSV
            </Button>
          </Stack>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ border: `1px solid ${tokens.colors.admin.border}`, borderRadius: 1, overflow: "hidden" }}>
        <Box sx={{ px: 3, pb: 2, pt: 3 }}>
          <DataTable
            columns={columns}
            rows={list.data?.items ?? []}
            rowCount={list.data?.total ?? 0}
            paginationModel={list.paginationModel}
            onPaginationModelChange={list.setPaginationModel}
            getRowId={(r) => r.id}
            loading={list.isLoading}
            bordered={false}
            rowActions={(row) => (
              <Button size="small" onClick={() => setOpenId(row.id)} sx={{ textTransform: "none" }}>
                View
              </Button>
            )}
          />
        </Box>
      </Paper>

      <Dialog open={openId !== null} onClose={() => setOpenId(null)} maxWidth="md" fullWidth>
        <DialogTitle>Withdrawal request</DialogTitle>
        <DialogContent>
          {detail.isLoading ? (
            <Box sx={{ p: 3 }}>Loading…</Box>
          ) : !w ? (
            <Box sx={{ p: 3 }}>Couldn&apos;t load.</Box>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Row label="Customer" value={`${w.user?.display_name ?? "—"} (${w.user?.email ?? "—"})`} />
              <Row label="Amount" value={formatHKD(w.amount_cents)} />
              <Row label="Fee" value={w.fee_cents > 0 ? formatHKD(w.fee_cents) : "—"} />
              <Row label="Status" value={w.status} />
              <Row label="Requested" value={new Date(w.requested_at).toLocaleString("en-HK")} />
              {w.processed_at ? (
                <Row label="Processed" value={new Date(w.processed_at).toLocaleString("en-HK")} />
              ) : null}
              {w.admin_notes ? <Row label="Admin notes" value={w.admin_notes} /> : null}
              {w.rejection_reason ? <Row label="Rejection reason" value={w.rejection_reason} /> : null}
              <Box sx={{ borderTop: `1px solid ${tokens.colors.admin.border}`, pt: 2, mt: 1 }}>
                <Box sx={{ fontWeight: 700, mb: 1 }}>Bank account</Box>
                <Row label="Bank" value={w.destination_details?.bank_name ?? "—"} />
                <Row label="Account holder" value={w.destination_details?.account_holder ?? "—"} />
                <Row label="Account number" value={w.destination_details?.account_number ?? "—"} mono />
                <Row label="Summary" value={w.destination_summary} />
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="contained" sx={greyBtn} onClick={() => setOpenId(null)}>
            Close
          </Button>
          {w?.status === "requested" ? (
            <Button
              variant="contained"
              sx={searchBtn}
              disabled={markProcessing.isPending}
              onClick={() => markProcessing.mutate()}
            >
              Mark processing
            </Button>
          ) : null}
          {w?.status === "processing" ? (
            <Button
              variant="contained"
              sx={greenBtn}
              disabled={markCompleted.isPending}
              onClick={() => markCompleted.mutate()}
            >
              Mark completed
            </Button>
          ) : null}
          {w && (w.status === "requested" || w.status === "processing") ? (
            <Button variant="contained" sx={redBtn} onClick={() => setRejectOpen(true)}>
              Reject + refund
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject withdrawal</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box sx={{ fontSize: 14, color: tokens.colors.admin.textMuted }}>
              The customer will be refunded and pushed with the reason.
            </Box>
            <TextField
              label="Reason"
              variant="standard"
              required
              fullWidth
              multiline
              minRows={3}
              inputProps={{ maxLength: 500 }}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" sx={greyBtn} onClick={() => setRejectOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={redBtn}
            disabled={!rejectReason.trim() || rejectMut.isPending}
            onClick={() => rejectMut.mutate()}
          >
            Reject + refund
          </Button>
        </DialogActions>
      </Dialog>

      {ToastEl}
    </Box>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      <Box sx={{ width: 140, color: tokens.colors.admin.textMuted, fontSize: 13 }}>{label}</Box>
      <Box sx={{ flex: 1, fontFamily: mono ? "monospace" : undefined, fontSize: 14 }}>{value}</Box>
    </Box>
  );
}
